import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper } from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BoltIcon from '@mui/icons-material/Bolt';
import PublicIcon from '@mui/icons-material/Public';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import LayersIcon from '@mui/icons-material/Layers';
import MapIcon from '@mui/icons-material/Map';
import HubIcon from '@mui/icons-material/Hub';

const CARDS = [
  {
    path: '/market/map', icon: MapIcon,
    title: 'Network Map',
    desc: 'Geographic map of NEM energy assets, substations and transmission lines. Search or click for details; assets show last-30-day generation.',
  },
  {
    path: '/market/transmission', icon: HubIcon,
    title: 'Transmission',
    desc: 'Interconnector flow vs transfer-limit utilisation over time, plus active transmission line & transformer outages.',
  },
  {
    path: '/market/generation', icon: LayersIcon,
    title: 'Generation Mix',
    desc: 'Stacked fleet generation by fuel technology over time — coal, gas, hydro, wind, solar, rooftop.',
  },
  {
    path: '/market/price', icon: ShowChartIcon,
    title: 'Price & Volatility',
    desc: 'Regional dispatch prices, price-band distribution, ≥$300 & negative-price frequency, intraday shape.',
  },
  {
    path: '/market/demand', icon: BoltIcon,
    title: 'Demand & Renewables',
    desc: 'Operational demand by region, renewable penetration, and the daily duck curve.',
  },
  {
    path: '/market/interconnectors', icon: PublicIcon,
    title: 'Interconnectors & Constraints',
    desc: 'Inter-regional flows and the network constraints binding most often.',
  },
  {
    path: '/market/fcas', icon: StackedLineChartIcon,
    title: 'FCAS Market',
    desc: 'Frequency-control ancillary-service prices by service and settled cost.',
  },
  {
    path: '/market/battery', icon: BatteryChargingFullIcon,
    title: 'Battery & Storage',
    desc: 'NEM battery fleet dispatch — charge/discharge, round-trip, FCAS enablement.',
  },
];

export default function MarketHome() {
  const navigate = useNavigate();

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{
          fontSize: '1.5rem', fontWeight: 700, color: 'text.primary',
          letterSpacing: '-0.025em', lineHeight: 1.1,
        }}>
          Market
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 0.5 }}>
          NEM market intelligence — real AEMO actuals. Open a dashboard:
        </Typography>
      </Box>

      <Box sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
      }}>
        {CARDS.map(({ path, icon: Icon, title, desc }) => (
          <Paper
            key={path}
            onClick={() => navigate(path)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(path); }}
            sx={{
              p: 3,
              cursor: 'pointer',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              boxShadow: 'none',
              transition: 'border-color 0.15s ease, transform 0.15s ease, background-color 0.15s ease',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'rgba(217, 138, 43, 0.04)',
                transform: 'translateY(-2px)',
              },
              '&:focus-visible': {
                outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2,
              },
            }}
          >
            <Box sx={{
              width: 44, height: 44, borderRadius: 2, mb: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(217, 138, 43, 0.10)',
            }}>
              <Icon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography sx={{
              fontSize: '1.05rem', fontWeight: 700, color: 'text.primary',
              letterSpacing: '-0.015em', mb: 0.75,
            }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.5 }}>
              {desc}
            </Typography>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}
